/**
 * Phone Number Screen for Ramps Flow
 *
 * Dedicated screen for entering phone number
 * Part of the sequential flow: Calculator → Name → Phone → Address → ID → Review → Status
 */

import { t } from "@lingui/core/macro";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { CircleFlag } from "react-circle-flags";
import type {
	PhonePreCheckRequest,
	SenderProfile,
} from "@/api/types/moneygram";
import { RampsButton } from "@/components/ramps/Button";
import { isBankService } from "@/domain/calculator";
import { useApiClientWithAutoVersion } from "@/hooks/useApiClientWithAutoVersion";
import { useContinue } from "@/hooks/useContinue";
import { useNavigateBack } from "@/hooks/useNavigateBack";
import { useAnalytics } from "@/hooks/usePostHog";
import { getAuthTokenFromSearchParams } from "@/lib/ramps-search-schema";
import { RampsInput } from "../components/ramps/Input";
import { RampsText } from "../components/ramps/Text";
import { Progress } from "../components/ui/progress";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from "../components/ui/select";
import {
	phoneStepSchema,
	phoneVerificationSchema,
	useRampsForm,
} from "../hooks/useRampsForm";
import { extractKycDataFromCompliance } from "../stores/ramps-store";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";

interface PhoneCountryOption {
	value: string; // dial code + country code combo for uniqueness
	dialCode: string; // the actual dial code like "+1"
	label: string; // country name
	countryCode: string; // iso2Code for flag
	iso3Code: string; // for backend compatibility
}

// Helper to find phone country option by ISO3 code
function findPhoneCountryByIso3(
	iso3Code: string,
	phoneCountryOptions: PhoneCountryOption[],
): PhoneCountryOption | undefined {
	// First try exact match
	const exactMatch = phoneCountryOptions.find(
		(opt) => opt.iso3Code === iso3Code,
	);
	if (exactMatch) return exactMatch;

	// For USA, prefer +1 dial code if multiple options exist
	if (iso3Code === "USA") {
		return phoneCountryOptions.find(
			(opt) => opt.iso3Code === "USA" && opt.dialCode === "1",
		);
	}

	return undefined;
}

function PhoneNumberPage() {
	const search = Route.useSearch();
	const store = useRampsStore();
	const form = useRampsForm();
	const handleContinue = useContinue();
	const handleBack = useNavigateBack();
	const { trackPageView, trackEvent } = useAnalytics();

	// Track page view
	useEffect(() => {
		trackPageView("phone", { page_name: "phone" });
	}, [trackPageView]);

	// Get API client with authentication (supports both v2 and v5 API)
	const authToken = getAuthTokenFromSearchParams(search) || store.token;
	const { apiClient } = useApiClientWithAutoVersion({
		authToken,
	});

	// Fetch countries using the same logic as calculator
	const countriesQuery = useQuery({
		queryKey: ["countries"],
		queryFn: async () => {
			const countries = await apiClient.referenceData.getCountries();
			return countries;
		},
		enabled: false,
	});

	// Access cached service options from calculator (no new fetch)
	const serviceOptionsQuery = useQuery({
		// Todo: Review: should this be updated to useQueryClient?
		queryKey: [
			"serviceOptions",
			store.operation,
			store.assetCode,
			store.payoutCountry,
		],
		queryFn: () =>
			apiClient.serviceOptions.getServiceOptions({
				operation: store.operation || "withdraw",
				assetCode: store.assetCode,
				userSelectedCountry: store.payoutCountry,
			}),
		enabled: false, // Never fetch - only use cached data from calculator
		staleTime: 2 * 60 * 1000, // Cache for 2 minutes (match calculator)
	});

	// Store payout country as source of truth for phone country selection
	const allCountries = countriesQuery.data || [];
	const allowedCountryCodes = serviceOptionsQuery.data?.cashOutCountries || [];
	const filteredCountries = useMemo(() => {
		return allowedCountryCodes.length > 0
			? allCountries.filter((country) =>
					allowedCountryCodes.includes(country.iso3Code),
				)
			: allCountries;
	}, [allCountries, allowedCountryCodes]);

	// Create phone country options using actual phoneDialCodes from API
	const phoneCountryOptions: PhoneCountryOption[] = useMemo(() => {
		return filteredCountries
			.filter(
				(country) =>
					country.phoneDialCodes && country.phoneDialCodes.length > 0,
			)
			.flatMap((country) =>
				country.phoneDialCodes.map((dialCode) => ({
					value: `${dialCode}-${country.iso3Code}`,
					dialCode: dialCode,
					label: country.name,
					countryCode: country.iso2Code.toLowerCase(),
					iso3Code: country.iso3Code,
				})),
			)
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [filteredCountries]);

	// Token and transaction_id are synced globally by URLParamsInitializer

	// Set initial phone country from payout country
	useEffect(() => {
		const hasCountries = phoneCountryOptions.length > 0;
		const hasPayoutCountry = !!store.payoutCountry;

		// Only initialize if store doesn't have a selection yet
		if (!store.phoneCountrySelection && hasCountries && hasPayoutCountry) {
			// Try to find phone country option matching payout country
			const phoneCountryForPayout = findPhoneCountryByIso3(
				store.payoutCountry,
				phoneCountryOptions,
			);
			const initial = phoneCountryForPayout || phoneCountryOptions[0];

			if (initial) {
				// Update store instead of URL
				rampsActions.setPhoneCountrySelection(initial.value);
				form.setFieldValue("phoneCountryCode", initial.value);
			}
		}
	}, [
		store.phoneCountrySelection,
		store.payoutCountry,
		phoneCountryOptions,
		form,
	]);

	// Initialize store phoneCountrySelection from existing phoneCountryCode if needed
	useEffect(() => {
		if (
			store.phoneCountryCode &&
			!store.phoneCountrySelection &&
			phoneCountryOptions.length > 0
		) {
			// Try to find matching option from existing dial code
			const dialCodeWithoutPlus = store.phoneCountryCode.replace("+", "");
			const matchingOption = phoneCountryOptions.find(
				(opt) => opt.dialCode === dialCodeWithoutPlus,
			);

			if (matchingOption) {
				rampsActions.setPhoneCountrySelection(matchingOption.value);
				form.setFieldValue("phoneCountryCode", matchingOption.value);
			}
		}
	}, [
		store.phoneCountryCode,
		store.phoneCountrySelection,
		phoneCountryOptions,
		form,
	]);

	// Sync form field with store selection
	useEffect(() => {
		if (store.phoneCountrySelection && phoneCountryOptions.length > 0) {
			const selectedOption = phoneCountryOptions.find(
				(opt) => opt.value === store.phoneCountrySelection,
			);
			if (
				selectedOption &&
				form.state.values.phoneCountryCode !== selectedOption.value
			) {
				form.setFieldValue("phoneCountryCode", selectedOption.value);
			}
		}
	}, [store.phoneCountrySelection, phoneCountryOptions, form]);

	// Helper to format phone numbers
	const formatPhoneNumber = (
		value: string,
		countryCodeValue: string | undefined,
	) => {
		// Clean input - only allow digits
		const cleaned = value.replace(/\D/g, "");

		// Extract dial code from form value (format: "dialCode-iso3Code" or just dial code)
		// e.g., "1-USA" -> "1", or "+1" -> "1"
		// Handle undefined/empty countryCodeValue gracefully
		if (!countryCodeValue) {
			// Default to 15 digits if country code not set yet
			return cleaned.slice(0, 15);
		}

		const dialCode = countryCodeValue.includes("-")
			? countryCodeValue.split("-")[0]
			: countryCodeValue.replace("+", "");

		// Flexible length based on country - most countries allow 7-15 digits
		// US/Canada (+1) allows 10 digits
		const maxLength = dialCode === "1" ? 10 : 15;
		return cleaned.slice(0, maxLength);
	};

	// Helper function to capitalize first letter of each word
	const capitalizeFirstLetter = (value: string) => {
		return value
			.split(" ")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(" ");
	};

	// Helper function to prevent numbers in name inputs
	const preventNumbers = (value: string) => {
		return value.replace(/[0-9]/g, "");
	};

	// Combined helper for name fields: capitalize and prevent numbers
	const formatNameInput = (value: string) => {
		return capitalizeFirstLetter(preventNumbers(value));
	};

	// Helper to format idLast4 (alphanumeric, max 4 characters)
	const formatIdLast4 = (value: string) => {
		// Only allow alphanumeric characters
		const cleaned = value.replace(/[^0-9A-Za-z]/g, "");
		// Max 4 characters
		return cleaned.slice(0, 4).toUpperCase();
	};

	// Helper function to get first error message from field
	const getFieldError = (
		fieldName:
			| keyof typeof phoneStepSchema.shape
			| keyof typeof phoneVerificationSchema.shape,
	) => {
		const field = form.getFieldMeta(fieldName);
		if (field?.errors && field.errors.length > 0) {
			const error = field.errors[0];
			return typeof error === "string" ? error : error.message || String(error);
		}
		return undefined;
	};

	// Phone validator that explicitly returns undefined when valid
	// This ensures errors clear immediately when validation passes
	// TanStack Form requires validators to return undefined (not null or empty array) to clear errors
	const phoneValidator = ({ value }: { value: unknown }) => {
		const result = phoneStepSchema.shape.phone.safeParse(value);
		// Explicitly return undefined when valid to clear errors immediately
		if (result.success) {
			return undefined;
		}
		// Return the first error message when invalid
		// Zod errors use 'issues' array, not 'errors'
		return result.error.issues[0]?.message;
	};

	// Loading state for phone pre-check
	const [isCheckingProfile, setIsCheckingProfile] = useState(false);
	// State for returning user verification
	const [showVerificationFields, setShowVerificationFields] = useState(false);
	const [isVerificationRequired, setIsVerificationRequired] = useState(false);
	const [isIdVerified, setIsIdVerified] = useState(false);
	const [verificationError, setVerificationError] = useState<string | null>(
		null,
	);

	/**
	 * Performs digital offramp profile lookup by phone number.
	 * Returns whether the submit flow should proceed.
	 */
	const performDigitalOfframpLookup = async (
		dialCode: string,
		phoneNumber: string,
	): Promise<boolean> => {
		setVerificationError(null);
		setIsCheckingProfile(true);

		let verificationRequired = isVerificationRequired;
		let idVerified = isIdVerified;

		try {
			// Build payload
			const payload: PhonePreCheckRequest = {
				phoneNumber,
				countryDialCode: dialCode,
			};

			if (showVerificationFields) {
				const verificationValidation = phoneVerificationSchema.safeParse(
					form.state.values,
				);

				if (!verificationValidation.success) {
					setIsCheckingProfile(false);
					return false;
				}

				payload.firstName = form.state.values.firstName;
				payload.lastName = form.state.values.lastName;
				payload.idLast4 = form.state.values.idLast4;
			}

			const preCheckResponse =
				await apiClient.profile.checkProfileByPhone(payload);

			if (
				preCheckResponse.digitalProfileId &&
				preCheckResponse.consumer.firstName
			) {
				idVerified = true;
				setIsIdVerified(true);

				rampsActions.setSenderProfile(
					preCheckResponse.consumer as SenderProfile,
				);
				// Check for step-up requirement and extract KYC data
				const kycData = extractKycDataFromCompliance(preCheckResponse);
				if (kycData) {
					rampsActions.setKycData(
						kycData.kycToken,
						kycData.kycApplicantId,
						"api",
					);

					// Track step-up requirement in analytics
					trackEvent("step_up_required", {
						source: "phone_lookup",
						action_provider: preCheckResponse.actionProvider || "",
					} as Record<string, unknown>);
				}
				if ("isStepUpRequired" in preCheckResponse) {
					rampsActions.setStepUpRequired(
						preCheckResponse.isStepUpRequired || false,
					);
				}
				if ("isAllowedToProceed" in preCheckResponse) {
					rampsActions.setIsAllowedToProceed(
						preCheckResponse.isAllowedToProceed || false,
					);
				}
				if (preCheckResponse.digitalProfileId) {
					// Existing user found by phone and ID verified
					rampsActions.setDigitalProfile(
						preCheckResponse.digitalProfileId,
						"phone",
					);

					// Fetch bank accounts for this profile
					const bankAccounts = await apiClient.profile.getProfileBankAccounts(
						preCheckResponse.digitalProfileId,
					);

					if (bankAccounts.bankAccounts.length > 0) {
						rampsActions.setSelectedBankAccount(bankAccounts.bankAccounts[0]);
					}
				}
			} else if (preCheckResponse.digitalProfileId) {
				setShowVerificationFields(true);
				setIsVerificationRequired(true);
				verificationRequired = true;
			}

			// Exit early if verification is required and ID is not verified
			if (verificationRequired && !idVerified) {
				console.warn("*** verification required and id not verified ***");
				return false;
			}

			rampsActions.setPhoneLookupCompleted(true);
			return true;
		} catch (error) {
			if (verificationRequired) {
				setVerificationError(
					t`We're sorry, but the details provided don't match an existing account for this phone number. We're unable to process your transaction at this time.`,
				);
				return false; // Exit early to prevent further processing
			}
			console.error(error);
			return true; // Continue on non-verification errors
		} finally {
			setIsCheckingProfile(false);
		}
	};

	const handleSubmit = async () => {
		// Validate current step before proceeding
		const stepValidation = phoneStepSchema.safeParse(form.state.values);

		if (!stepValidation.success) {
			return;
		}

		// Use store as source of truth for phone country
		const phoneCountryValue =
			store.phoneCountrySelection || form.state.values.phoneCountryCode;
		const selectedCountry = phoneCountryOptions.find(
			(opt) => opt.value === phoneCountryValue,
		);

		// For digitalOfframp services, check for existing profile by phone
		if (
			isBankService(store.serviceOption) &&
			authToken &&
			!store.digitalProfileId &&
			!store.phoneLookupCompleted
		) {
			const dialCode = (selectedCountry?.dialCode || phoneCountryValue).replace(
				"+",
				"",
			);

			const shouldProceed = await performDigitalOfframpLookup(
				dialCode,
				form.state.values.phone,
			);

			if (!shouldProceed) {
				return;
			}
		}

		// Save phone data to store
		rampsActions.setPhoneData({
			phone: form.state.values.phone,
			phoneCountryCode: selectedCountry?.dialCode || "+1",
			phoneCountrySelection: phoneCountryValue,
		});

		rampsActions.setCurrentStep("name");
		handleContinue(search);
	};

	return (
		<div className="flex flex-col min-h-screen max-w-screen-sm mx-auto bg-ramps-background-base">
			{/* Header */}
			<div className="flex items-center justify-between p-4">
				<button
					type="button"
					onClick={handleBack}
					className="w-8 h-8 flex items-center justify-center hover:opacity-80 transition-opacity"
					aria-label="Back"
				>
					<ArrowLeft className="w-6 h-6 text-ramps-text-primary" />
				</button>
			</div>

			{/* Progress bar */}
			<div className="px-4 mb-8">
				<Progress
					value={25}
					className="h-1 bg-ramps-background-surface-2 *:bg-ramps-text-primary"
				/>
			</div>

			{/* Content */}
			<div className="flex-1 px-4 flex flex-col">
				<div className="text-left mb-2">
					<RampsText variant="title-lg-bold" color="primary">
						{t`Your phone number`}
					</RampsText>
				</div>

				{/* Phone Form */}
				<div className="flex-1 space-y-6">
					{/* Phone Section */}
					<div className="space-y-4">
						{/* Phone Input Field - Single instance for input and error display */}
						<form.Field
							name="phone"
							validators={{
								// Custom validator that explicitly returns undefined when valid
								// This ensures errors clear immediately when validation passes
								onChange: phoneValidator,
								onBlur: phoneValidator,
							}}
						>
							{(phoneField) => {
								const error = phoneField.state.meta.errors[0];
								return (
									<div className="space-y-2">
										<div className="flex gap-3">
											{/* Country Selector */}
											<form.Field
												name="phoneCountryCode"
												validators={{
													onChange: phoneStepSchema.shape.phoneCountryCode,
												}}
											>
												{(countryField) => {
													// Use store as source of truth, fallback to form field
													const currentValue =
														store.phoneCountrySelection ||
														countryField.state.value;
													const selectedCountry = phoneCountryOptions.find(
														(c) => c.value === currentValue,
													);

													return (
														<div className="bg-ramps-background-surface-2 rounded-lg p-2 pt-1 shrink-0">
															<Select
																value={currentValue}
																onValueChange={(value) => {
																	// Update store and form
																	rampsActions.setPhoneCountrySelection(value);
																	countryField.handleChange(value);
																}}
																disabled={countriesQuery.isLoading}
															>
																<SelectTrigger className="w-auto border-none bg-transparent mt-2 h-auto gap-2 shadow-none">
																	<div className="flex items-center gap-2">
																		{countriesQuery.isLoading ? (
																			<div className="w-6 h-6 rounded-full bg-gray-300 animate-pulse" />
																		) : (
																			<CircleFlag
																				countryCode={
																					selectedCountry?.countryCode || "us"
																				}
																				className="w-6 h-6"
																			/>
																		)}
																		<RampsText
																			variant="body-default"
																			color="primary"
																			className="font-medium"
																		>
																			{countriesQuery.isLoading
																				? "..."
																				: `+${selectedCountry?.dialCode}` ||
																					"+1"}
																		</RampsText>
																	</div>
																</SelectTrigger>
																<SelectContent className="bg-ramps-background-base border-ramps-border-default">
																	{countriesQuery.isLoading ? (
																		<SelectItem value="loading" disabled>
																			<RampsText
																				variant="body-default"
																				color="secondary"
																			>
																				{t`Loading countries...`}
																			</RampsText>
																		</SelectItem>
																	) : countriesQuery.error ? (
																		<SelectItem value="error" disabled>
																			<RampsText
																				variant="body-default"
																				color="secondary"
																			>
																				{t`Error loading countries`}
																			</RampsText>
																		</SelectItem>
																	) : phoneCountryOptions.length === 0 ? (
																		<SelectItem value="no-countries" disabled>
																			<RampsText
																				variant="body-default"
																				color="secondary"
																			>
																				{t`No countries available`}
																			</RampsText>
																		</SelectItem>
																	) : (
																		phoneCountryOptions.map((country) => (
																			<SelectItem
																				key={country.value}
																				value={country.value}
																			>
																				<div className="flex items-center gap-3">
																					<CircleFlag
																						countryCode={country.countryCode}
																						className="w-5 h-5"
																					/>
																					<RampsText
																						variant="body-sm"
																						color="primary"
																					>
																						{`+${country.dialCode}`}{" "}
																						{country.label}
																					</RampsText>
																				</div>
																			</SelectItem>
																		))
																	)}
																</SelectContent>
															</Select>
														</div>
													);
												}}
											</form.Field>

											{/* Phone Input */}
											<div className="flex-1">
												<RampsInput
													label={t`Phone Number`}
													type="tel"
													value={phoneField.state.value}
													onChange={(e) => {
														const countryCode =
															form.getFieldValue("phoneCountryCode");
														const formatted = formatPhoneNumber(
															e.target.value,
															countryCode,
														);
														// Set the formatted value - this will trigger onChange validation automatically
														phoneField.handleChange(formatted);
														// Reset verification fields when phone number changes
														setShowVerificationFields(false);
														setVerificationError(null);
														form.setFieldValue("firstName", "");
														form.setFieldValue("lastName", "");
														form.setFieldValue("idLast4", "");
														rampsActions.setPhoneLookupCompleted(false);
														rampsActions.setDigitalProfileId("");
														setIsVerificationRequired(false);
														setIsIdVerified(false);
													}}
													onBlur={phoneField.handleBlur}
													// Don't pass error to avoid UI displacement
												/>
											</div>
										</div>
										{/* Error display - below input, same field instance ensures immediate updates */}
										{error ? (
											<div className="flex items-start">
												<RampsText
													variant="body-sm"
													color="error"
													className="text-left"
												>
													*
													{typeof error === "string"
														? error
														: error &&
																typeof error === "object" &&
																"message" in error
															? (error as { message: string }).message
															: String(error)}
												</RampsText>
											</div>
										) : null}
										{/* Disclaimer text - right below error or input */}
										<RampsText
											variant="body-sm"
											color="secondary"
											className="text-left leading-relaxed"
										>
											{t`Message and data rates may apply.`}
										</RampsText>
									</div>
								);
							}}
						</form.Field>

						{/* Verification Fields for Returning Users */}
						{showVerificationFields && (
							<div className="space-y-4 mt-6 pt-6 border-t border-ramps-border-default">
								<RampsText
									variant="body-default"
									color="primary"
									className="text-left mb-4"
								>
									{t`Please verify your identity to continue`}
								</RampsText>

								<form.Field
									name="firstName"
									validators={{
										onChange: phoneVerificationSchema.shape.firstName,
									}}
								>
									{(field) => (
										<RampsInput
											label={t`First Name`}
											type="text"
											value={field.state.value}
											onChange={(e) => {
												if (verificationError) setVerificationError(null);
												field.handleChange(formatNameInput(e.target.value));
											}}
											onBlur={field.handleBlur}
											error={getFieldError("firstName")}
										/>
									)}
								</form.Field>

								<form.Field
									name="lastName"
									validators={{
										onChange: phoneVerificationSchema.shape.lastName,
									}}
								>
									{(field) => (
										<RampsInput
											label={t`Last Name`}
											type="text"
											value={field.state.value}
											onChange={(e) => {
												if (verificationError) setVerificationError(null);
												field.handleChange(formatNameInput(e.target.value));
											}}
											onBlur={field.handleBlur}
											error={getFieldError("lastName")}
										/>
									)}
								</form.Field>

								<form.Field
									name="idLast4"
									validators={{
										onChange: phoneVerificationSchema.shape.idLast4,
									}}
								>
									{(field) => (
										<RampsInput
											label={t`Last 4 digits of ID`}
											type="text"
											value={field.state.value}
											onChange={(e) => {
												if (verificationError) setVerificationError(null);
												field.handleChange(formatIdLast4(e.target.value));
											}}
											onBlur={field.handleBlur}
											error={getFieldError("idLast4")}
											maxLength={4}
										/>
									)}
								</form.Field>

								{/* Verification Error Message */}
								{verificationError && (
									<div className="mt-2">
										<RampsText
											variant="body-sm"
											color="error"
											className="text-left"
										>
											{verificationError}
										</RampsText>
									</div>
								)}
							</div>
						)}
					</div>
				</div>

				{/* Continue Button */}
				<div className="pt-8 pb-6">
					<form.Subscribe
						selector={(state) => {
							if (showVerificationFields) {
								return phoneVerificationSchema.safeParse(state.values).success;
							}
							return phoneStepSchema.safeParse(state.values).success;
						}}
					>
						{(isStepValid) => (
							<RampsButton
								onClick={handleSubmit}
								disabled={!isStepValid || isCheckingProfile}
								className="w-full h-12 rounded-lg font-medium bg-black text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{isCheckingProfile ? t`Checking...` : t`Continue`}
							</RampsButton>
						)}
					</form.Subscribe>
				</div>
			</div>
		</div>
	);
}

export const Route = createFileRoute("/ramps/phone")({
	validateSearch: (search) =>
		search as {
			step?: string;
			transaction_id?: string;
			token?: string;
			accesstoken?: string;
		},
	component: PhoneNumberPage,
});
